<script setup lang="ts">
import { Icon } from '@iconify/vue';
import multipleChatsGif from '@/assets/images/multiple-chats.gif';
import projectDemoGif from '@/assets/images/project-demo.gif';
import searchVideo from '@/assets/images/search-2.mp4';

withDefaults(defineProps<{
   /**
    * Controls which content to show.
    * - "popup": Only new features (Search 2.0, Luma 2)
    * - "full": All content including legacy features
    */
   variant?: 'popup' | 'full';
}>(), {
   variant: 'full',
});

// Search 2.0 highlights
const searchHighlights = [
   {
      icon: 'ph:lightning',
      title: 'Faster Results',
      description: 'Get the documents you need in less time with a significantly faster search engine.',
   },
   {
      icon: 'ph:chat-text',
      title: 'Natural Language Search',
      description: 'Search the way you think — type questions or phrases in plain English and get relevant results.',
   },
   {
      icon: 'ph:sort-ascending',
      title: 'Relevance Ranking',
      description: 'Results are ranked by relevance so the most important documents surface first.',
   },
   {
      icon: 'ph:magnifying-glass',
      title: 'Classic Search Experience',
      description: 'A familiar, intuitive search interface designed for quick and efficient document discovery.',
   },
   {
      icon: 'ph:funnel',
      title: 'Filter Groups',
      description: 'Easily narrow results with organized filter groups — find exactly what you need in fewer clicks.',
   },
   {
      icon: 'ph:sidebar',
      title: 'Collapsible Sidebar',
      description: 'Collapse the sidebar to maximize screen real estate when you need more room for results.',
   },
];

// Luma 2 pillars
const luma2Pillars = [
   {
      title: 'Smarter Answers',
      description: 'Luma now thinks through your question step by step, double-checks its work, and tries again if something doesn\'t look right — so you can trust what you get back.',
   },
   {
      title: 'Built-In Workflows',
      description: 'Summarize a facility, compare sites, build a timeline, review violations, and more — Luma better understands your questions to suggest the appropriate tools.',
   },
   {
      title: 'Cited Sources',
      description: 'Every answer displays cited documents so you can validate where the answer came from.',
   },
];

// Legacy features (Learning Library only)
const projectsFeature = {
   title: 'Projects',
   description: 'Organize documents into collaborative Projects. Create private collections for personal work and add documents from search results or your Saved Documents with a single click.',
   image: projectDemoGif,
};

const multipleChatsFeature = {
   title: 'Multiple Chats',
   description: 'Start and manage multiple conversation threads with Luma AI simultaneously. Each chat maintains its own context, allowing you to work on different topics or questions in parallel without losing track of your conversations.',
   image: multipleChatsGif,
};

const bulkExportFeature = {
   title: 'Bulk Actions & Export',
   description: 'Select multiple documents using checkboxes, then save, add to project, or export them as a ZIP file — all at once.',
};
</script>

<template>
   <div class="space-y-6" role="region" aria-label="What's New in Project Velocity">
      <!-- ============================================ -->
      <!-- SEARCH 2.0 (both popup and full) -->
      <!-- ============================================ -->
      <section class="bg-neutral rounded-2xl p-6" aria-labelledby="search-heading">
         <div class="flex items-center gap-3 mb-2">
            <div class="size-10 rounded-xl bg-primary/10 flex items-center justify-center" aria-hidden="true">
               <Icon icon="ph:magnifying-glass" class="size-6 text-primary" />
            </div>
            <div>
               <h3 id="search-heading" class="text-2xl font-bold text-black">
                  Search 2.0
               </h3>
               <p class="text-sm text-neutral-darker">
                  A completely redesigned search experience
               </p>
            </div>
         </div>
         <p class="text-base text-neutral-darker leading-relaxed max-w-3xl mb-6">
            Search has been rebuilt from the ground up — faster, smarter, and easier to use. Find the documents you need with natural language queries, relevance-based results, and powerful filtering.
         </p>

         <div class="rounded-2xl overflow-hidden border border-neutral-dark bg-white mb-6">
            <video
               :src="searchVideo"
               autoplay
               loop
               muted
               playsinline
               aria-label="Demonstration of the new Search 2.0 interface"
               class="w-full h-auto"
            />
         </div>

         <ul class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 list-none p-0" aria-label="Search 2.0 features">
            <li
               v-for="highlight in searchHighlights"
               :key="highlight.title"
               class="bg-white rounded-2xl border border-neutral-dark p-4"
            >
               <div class="flex items-center gap-3 mb-2">
                  <div class="size-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                     <Icon :icon="highlight.icon" class="size-5 text-primary" />
                  </div>
                  <h4 class="font-semibold text-black">
                     {{ highlight.title }}
                  </h4>
               </div>
               <p class="text-sm text-neutral-darker leading-relaxed">
                  {{ highlight.description }}
               </p>
            </li>
         </ul>
      </section>

      <!-- ============================================ -->
      <!-- LUMA 2 (both popup and full) -->
      <!-- ============================================ -->
      <section class="bg-neutral rounded-2xl p-6" aria-labelledby="luma2-heading">
         <div class="flex items-center gap-3 mb-2">
            <div class="size-10 rounded-xl bg-primary/10 flex items-center justify-center" aria-hidden="true">
               <Icon icon="ph:robot" class="size-6 text-primary" />
            </div>
            <div>
               <h3 id="luma2-heading" class="text-2xl font-bold text-black">
                  Luma 2
               </h3>
               <p class="text-sm text-neutral-darker">
                  Next-generation AI agent — shipped April 2026
               </p>
            </div>
         </div>
         <p class="text-base text-neutral-darker leading-relaxed max-w-3xl mb-6">
            Built to be trusted by regulators. Three design choices make Luma trustworthy for regulatory work.
         </p>

         <ul class="grid grid-cols-1 md:grid-cols-3 gap-4 list-none p-0" aria-label="Luma 2 highlights">
            <li
               v-for="pillar in luma2Pillars"
               :key="pillar.title"
               class="bg-white rounded-2xl border border-neutral-dark p-6"
            >
               <h4 class="font-semibold text-black mb-2">
                  {{ pillar.title }}
               </h4>
               <p class="text-sm text-neutral-darker leading-relaxed">
                  {{ pillar.description }}
               </p>
            </li>
         </ul>
      </section>

      <!-- ============================================ -->
      <!-- LEGACY CONTENT BELOW (Learning Library only) -->
      <!-- ============================================ -->
      <template v-if="variant === 'full'">
         <!-- Latest Features Section -->
         <section class="bg-neutral rounded-2xl p-6" aria-labelledby="features-heading">
            <div class="flex items-center gap-2 mb-4">
               <Icon icon="ph:sparkle" class="size-6 text-primary" aria-hidden="true" />
               <h3 id="features-heading" class="text-xl font-semibold text-black">
                  Latest Features
               </h3>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
               <!-- Projects - spans 2 columns and 2 rows -->
               <div class="md:col-span-2 md:row-span-2 bg-white rounded-xl p-4 shadow-sm border border-neutral-dark flex flex-col">
                  <div class="rounded-lg overflow-hidden border border-neutral-light bg-neutral mb-3">
                     <img
                        :src="projectsFeature.image"
                        :alt="`${projectsFeature.title} feature demonstration`"
                        class="w-full h-auto"
                     />
                  </div>
                  <h4 class="font-semibold text-lg text-black mb-2">
                     {{ projectsFeature.title }}
                  </h4>
                  <p class="text-sm text-neutral-darker">
                     {{ projectsFeature.description }}
                  </p>
               </div>

               <!-- Saved Documents -->
               <div class="bg-white rounded-xl p-4 shadow-sm border border-neutral-dark flex flex-col">
                  <div class="flex-1 bg-neutral rounded-lg p-3 flex items-center justify-center mb-3">
                     <Icon icon="ph:star" class="size-12 text-primary" aria-hidden="true" />
                  </div>
                  <h4 class="font-semibold text-lg text-black mb-2">
                     Saved Documents
                  </h4>
                  <p class="text-sm text-neutral-darker">
                     Star documents to save them for quick access later. Your Saved Documents page shows all your bookmarked items in one place.
                  </p>
               </div>

               <!-- Bulk Actions & Export -->
               <div class="bg-white rounded-xl p-4 shadow-sm border border-neutral-dark flex flex-col">
                  <div class="flex-1 bg-neutral rounded-lg p-3 flex items-center justify-center mb-3">
                     <svg viewBox="0 0 140 60" class="w-full max-w-[140px]" aria-hidden="true">
                        <rect x="5" y="5" width="45" height="50" rx="3" fill="#E5E7EB" stroke="#D1D5DB" stroke-width="1" />
                        <rect x="10" y="10" width="8" height="8" rx="2" fill="#024D50" />
                        <path d="M12 14 L14 16 L17 12" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round" />
                        <rect x="22" y="11" width="22" height="4" rx="1" fill="#D1D5DB" />
                        <rect x="10" y="22" width="35" height="3" rx="1" fill="#D1D5DB" />
                        <rect x="10" y="28" width="30" height="3" rx="1" fill="#D1D5DB" />
                        <rect x="10" y="38" width="8" height="8" rx="2" fill="#024D50" />
                        <path d="M12 42 L14 44 L17 40" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round" />
                        <rect x="22" y="39" width="18" height="4" rx="1" fill="#D1D5DB" />
                        <rect x="22" y="46" width="24" height="3" rx="1" fill="#D1D5DB" />
                        <path d="M58 30 L73 30" stroke="#024D50" stroke-width="2" stroke-linecap="round" />
                        <path d="M68 25 L75 30 L68 35" stroke="#024D50" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" />
                        <rect x="85" y="10" width="45" height="40" rx="4" fill="#024D50" opacity="0.15" stroke="#024D50" stroke-width="1.5" />
                        <rect x="100" y="10" width="12" height="40" fill="#024D50" opacity="0.1" />
                        <rect x="102" y="14" width="8" height="4" rx="1" fill="#024D50" opacity="0.4" />
                        <rect x="102" y="22" width="8" height="4" rx="1" fill="#024D50" opacity="0.4" />
                        <rect x="102" y="30" width="8" height="4" rx="1" fill="#024D50" opacity="0.4" />
                        <rect x="102" y="38" width="8" height="4" rx="1" fill="#024D50" opacity="0.4" />
                        <text x="107" y="52" font-size="6" fill="#024D50" font-weight="600" text-anchor="middle">.ZIP</text>
                     </svg>
                  </div>
                  <h4 class="font-semibold text-lg text-black mb-2">
                     {{ bulkExportFeature.title }}
                  </h4>
                  <p class="text-sm text-neutral-darker">
                     {{ bulkExportFeature.description }}
                  </p>
               </div>
            </div>
         </section>

         <!-- More Features Section -->
         <section class="bg-neutral rounded-2xl p-6" aria-labelledby="more-features-heading">
            <div class="flex items-center gap-2 mb-4">
               <Icon icon="ph:sparkle" class="size-6 text-primary" aria-hidden="true" />
               <h3 id="more-features-heading" class="text-xl font-semibold text-black">
                  More Features
               </h3>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
               <!-- Multiple Chats - spans 2 columns and 2 rows -->
               <div class="md:col-span-2 md:row-span-2 bg-white rounded-xl p-4 shadow-sm border border-neutral-dark flex flex-col">
                  <div class="rounded-lg overflow-hidden border border-neutral-light bg-neutral mb-3">
                     <img
                        :src="multipleChatsFeature.image"
                        alt="Multiple Chats feature demonstration"
                        class="w-full h-auto"
                     />
                  </div>
                  <h4 class="font-semibold text-lg text-black mb-2">
                     {{ multipleChatsFeature.title }}
                  </h4>
                  <p class="text-sm text-neutral-darker">
                     {{ multipleChatsFeature.description }}
                  </p>
               </div>

               <!-- Document Sorting -->
               <div class="bg-white rounded-xl p-4 shadow-sm border border-neutral-dark flex flex-col">
                  <div class="flex-1 bg-neutral rounded-lg p-3 flex items-center justify-center mb-3">
                     <svg viewBox="0 0 140 60" class="w-full max-w-[140px]" aria-hidden="true">
                        <rect x="10" y="5" width="80" height="14" rx="3" fill="#E5E7EB" />
                        <rect x="14" y="9" width="40" height="6" rx="2" fill="#D1D5DB" />
                        <rect x="10" y="23" width="80" height="14" rx="3" fill="#E5E7EB" />
                        <rect x="14" y="27" width="50" height="6" rx="2" fill="#D1D5DB" />
                        <rect x="10" y="41" width="80" height="14" rx="3" fill="#E5E7EB" />
                        <rect x="14" y="45" width="35" height="6" rx="2" fill="#D1D5DB" />
                        <polygon points="120,8 115,14 125,14" fill="#024D50" />
                        <polygon points="120,52 115,46 125,46" fill="#024D50" />
                        <line x1="120" y1="12" x2="120" y2="48" stroke="#024D50" stroke-width="3" stroke-linecap="round" />
                     </svg>
                  </div>
                  <h4 class="font-semibold text-lg text-black mb-2">
                     Document Sorting
                  </h4>
                  <p class="text-sm text-neutral-darker">
                     Sort documents in ascending or descending order by metadata fields, including dates.
                  </p>
               </div>

               <!-- Quick Metadata Filters -->
               <div class="bg-white rounded-xl p-4 shadow-sm border border-neutral-dark flex flex-col">
                  <div class="flex-1 bg-neutral rounded-lg p-3 flex items-center justify-center mb-3">
                     <svg viewBox="0 0 140 60" class="w-full max-w-[140px]" aria-hidden="true">
                        <rect x="5" y="5" width="60" height="50" rx="4" fill="#E5E7EB" stroke="#D1D5DB" stroke-width="1" />
                        <rect x="10" y="12" width="30" height="5" rx="2" fill="#D1D5DB" />
                        <rect x="10" y="22" width="45" height="4" rx="1" fill="#D1D5DB" />
                        <rect x="10" y="30" width="40" height="4" rx="1" fill="#D1D5DB" />
                        <rect x="10" y="40" width="28" height="10" rx="5" fill="#024D50" opacity="0.6" />
                        <rect x="14" y="43" width="20" height="4" rx="2" fill="white" opacity="0.9" />
                        <path d="M75 30 L90 30" stroke="#024D50" stroke-width="2" stroke-linecap="round" />
                        <path d="M85 25 L92 30 L85 35" stroke="#024D50" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" />
                        <rect x="100" y="10" width="35" height="18" rx="3" fill="#024D50" opacity="0.2" stroke="#024D50" stroke-width="1" />
                        <rect x="104" y="15" width="20" height="4" rx="2" fill="#024D50" opacity="0.5" />
                        <rect x="100" y="34" width="35" height="18" rx="3" fill="#024D50" opacity="0.2" stroke="#024D50" stroke-width="1" />
                        <rect x="104" y="39" width="18" height="4" rx="2" fill="#024D50" opacity="0.5" />
                     </svg>
                  </div>
                  <h4 class="font-semibold text-lg text-black mb-2">
                     Quick Metadata Filters
                  </h4>
                  <p class="text-sm text-neutral-darker">
                     Filter directly from document cards by clicking metadata values, including source folder.
                  </p>
               </div>
            </div>
         </section>
      </template>
   </div>
</template>
